import { useEffect, useRef, useMemo } from "react";
import QRCode from "react-qr-code";
import styled from "styled-components"
import { debug } from "rtUtils/logger";
import { serializeProductConfigs } from "../../utils";
import { serializeArParams } from "../../utils/arUtils";
import {
	useConfigModelUrl,
	useConfigProductConfig,
	useConfigIsCustomQrModalRender,
	useConfigAr,
	useConfigCloudName,
} from "../../hooks/config";
import useShowCustomQrModal from "../../hooks/useShowCustomQrModal";
import useAnalytics from "../../hooks/useAnalytics";
import useOnQrModalClose from "../../hooks/useOnQrModalClose";
import Modal from "../Modal";

const buildQrUrl = (modelUrl, fallback, params, productConfig, cloudName) => {
	const qrUrlBuilder = new URL(location);
	qrUrlBuilder.search = `appMode=qr&cloudName=${cloudName}&model=${modelUrl}&fallback=${fallback}&params=${serializeArParams(params)}&productConfig=${productConfig}`;

	const qrUrl = qrUrlBuilder.href;
	debug("Generating QR for URL ", qrUrl);
	return qrUrl;
};

// const CopyButton = styled(Button)`
// 		font-size: 14px;
//     color: #69abee;
//     background-color: transparent;
//
//     &:hover {
//         background-color: transparent;
//         color: #b9d5f1
//     }
// `;

const UrlBox =  styled.input`
    font-size: 14px;
    background-color: transparent;
    max-width: 400px;
    width: 60%;
    line-height: 20px;
    color: var(--d8s-secondary);
    border: none;
    outline: none;
`;

const CodeWrapper = styled.div`
    width: 100%;
    height: 100%;
    max-height: 80%;
    display: flex;
    justify-content: center;
    align-items: center;
		
		/** Need to create a white background behind and slightly around the QR code for the camera to pick it up  **/
    &:before {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        height: 60%;
        width: auto;
        max-width: 450px;
        min-width: 300px;,
        max-height: 60%;
        aspect-ratio: 1/1;
		    background: white;
		    z-index: 1;
		    box-sizing: content-box;
		    ${props => props.$hide ? "display: none;" : ""}
    }
`;

const QrWrapper = ({ url, onClose }) => {
	const qrRef = useRef(null);
	const isCustomModalRendering = useConfigIsCustomQrModalRender();
	const { triggerShowCustomQrModal } = useShowCustomQrModal()
	const unregisterModalCloseHandler = useOnQrModalClose(onClose);
	const { triggerAnalyticsEvent, ANALYTICS_EVENTS } = useAnalytics();

	useEffect(() => {
		//unregister handler on unmount
		return unregisterModalCloseHandler();
	}, []);

	useEffect(() => {
		triggerAnalyticsEvent(ANALYTICS_EVENTS.AR_INTERACTION, { info: "show-qr-modal", customModal: isCustomModalRendering, url });

		if (isCustomModalRendering) {
			triggerShowCustomQrModal({
				url,
				qr: qrRef.current?.outerHTML
			});
		}
	}, [isCustomModalRendering, url]);

	//we always render the qr code to show it or provide it to the client's custom modal
	return (
		<CodeWrapper $hide={isCustomModalRendering}>
			<QRCode
				ref={qrRef}
				size={512}
				style={{
					width: "auto",
					height: "65%",
					maxWidth: "425px",
					minWidth: "300px",
					maxHeight: "65%",
					aspectRatio: "1/1",
					display: isCustomModalRendering ? "none" : "block",
					zIndex: 10,
				}}
				value={url}
				viewBox={`0 0 256 256`}
			/>
		</CodeWrapper>
	);
};

const StyledModal = styled(Modal)`
		h2 {
				margin: 0;
		}
`;

const ArQrModal = ({ modelUrl, fallbackUrl, params, productConfig, isOpen, onClose }) => {
	const cloudName = useConfigCloudName();4
	const isCustomModalRendering = useConfigIsCustomQrModalRender();
	const url = buildQrUrl(modelUrl, fallbackUrl, params, productConfig, cloudName);

	//TODO: make copy work inside iframe
	// const copyOnClick = () => {
	// 	navigator.clipboard.writeText(url)
	// };

	const qrWrapper = <QrWrapper
		url={url}
		onClose={onClose}
	/>;

	return ( isCustomModalRendering ? qrWrapper :
			<StyledModal
				isOpen={isOpen}
				onClose={onClose}
			>
				<h2>Scan the QR to view using your AR enabled device</h2>

				{qrWrapper}

				<UrlBox value={url} readOnly onFocus={(e) => e.target.select()}/>

				{/*<CopyButton onClick={copyOnClick}>Copy</CopyButton>*/}
			</StyledModal>
	);
};

const ArQrModalFromConfig = ({ isOpen, onClose }) => {
	const modelUrl = useConfigModelUrl();
	const { fallbackUrl, placement, title, link, resizable } = useConfigAr();
	const productConfigs = useConfigProductConfig();
	const serializedConfigs = productConfigs ? serializeProductConfigs(productConfigs) : "";

	const arParams = useMemo(() => ({ placement, title, link, resizable }) , [placement, title, link, resizable]);

	return (
		<ArQrModal
			modelUrl={modelUrl}
			fallbackUrl={fallbackUrl}
			params={arParams}
			productConfig={serializedConfigs}
			isOpen={isOpen}
			onClose={onClose}
		/>
	);
};

export {
	ArQrModal,
	ArQrModalFromConfig,
};
